import { zodResolver } from '@hookform/resolvers/zod';
import {
  ContactMessageInputSchema,
  type ContactMessageInput,
} from '@resolute/schema';
import { Button, Checkbox, Input } from '@resolute/ui';
import { motion, useReducedMotion } from 'framer-motion';
import { CheckCircle2, Send } from 'lucide-react';
import { useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { Link } from 'react-router-dom';


import { useSubmitContact } from '../api';

import { FormShell } from '@/components/form-shell';
import { PhoneField } from '@/components/phone-field';
import { RequestError } from '@/lib/api-client';
import { EASE_OUT_EXPO } from '@/lib/motion';


const MESSAGE_MAX = 2000;


export function ContactForm() {
  const reduce = useReducedMotion();
  const mutation = useSubmitContact();

  const {
    register,
    control,
    handleSubmit,
    watch,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<ContactMessageInput>({
    resolver: zodResolver(ContactMessageInputSchema),
    mode: 'onTouched',
    reValidateMode: 'onChange',
    defaultValues: {
      fullName: '',
      email: '',
      phone: '',
      company: '',
      message: '',
      consent: false as unknown as true,
      marketingConsent: false,
      website: '',
    },
  });

  const messageValue = watch('message') ?? '';
  const messageCount = messageValue.length;

  const onSubmit = handleSubmit(async (values) => {
    try {
      await mutation.mutateAsync(values);
      reset();
    } catch {
      // Surfaced below via mutation.isError; keeps values so the user can retry.
    }
  });

  const errorMessage = useMemo(() => {
    if (!mutation.isError) return null;
    return mutation.error instanceof RequestError
      ? mutation.error.message
      : 'Something went wrong sending your message. Please try again.';
  }, [mutation.isError, mutation.error]);

  if (mutation.isSuccess) {
    return <ContactSuccess reduce={reduce ?? false} onReset={() => mutation.reset()} />;
  }

  return (
    <FormShell
      onSubmit={onSubmit}
      ariaLabel="Contact form"
      honeypot={register('website')}
      error={errorMessage}
      reduce={reduce ?? false}
      footer={
        <Button
          type="submit"
          size="lg"
          loading={isSubmitting || mutation.isPending}
          className="w-full sm:w-auto sm:self-start"
        >
          {!(isSubmitting || mutation.isPending) && <Send className="size-4" aria-hidden="true" />}
          Submit
        </Button>
      }
    >
      <div className="grid gap-5 sm:grid-cols-2">
        <Input
          label="Full name"
          autoComplete="name"
          placeholder="Your full name"
          required
          error={errors.fullName?.message}
          {...register('fullName')}
        />
        <Input
          label="Email address"
          type="email"
          autoComplete="email"
          placeholder="you@example.com"
          required
          error={errors.email?.message}
          {...register('email')}
        />
        <PhoneField
          control={control}
          name="phone"
          label="Phone number"
          required
          error={errors.phone?.message}
        />
        <Input
          label="Company"
          autoComplete="organization"
          placeholder="Company name"
          error={errors.company?.message}
          {...register('company')}
        />
      </div>

      <div className="flex flex-col gap-1.5">
        <div className="flex items-baseline justify-between">
          <label htmlFor="contact-message" className="text-sm font-medium text-foreground">
            Message
          </label>
          <span
            className="text-xs tabular-nums text-muted-foreground"
            aria-hidden="true"
          >
            {messageCount}/{MESSAGE_MAX}
          </span>
        </div>
        <textarea
          id="contact-message"
          rows={5}
          maxLength={MESSAGE_MAX}
          className="w-full rounded border border-input bg-background px-3.5 py-2.5 text-sm text-foreground shadow-sm transition-[color,box-shadow,border-color] duration-fast ease-out-expo placeholder:text-muted-foreground hover:border-foreground/20 focus-visible:border-ring focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/60 focus-visible:ring-offset-1 focus-visible:ring-offset-background aria-[invalid=true]:border-danger aria-[invalid=true]:ring-danger/40"
          placeholder="Tell us what you're looking for — budget, location, timeline…"
          aria-invalid={errors.message ? true : undefined}
          aria-describedby={errors.message ? 'contact-message-error' : undefined}
          {...register('message')}
        />
        {errors.message?.message && (
          <p id="contact-message-error" className="text-xs text-danger" role="alert">
            {errors.message.message}
          </p>
        )}
      </div>

      <div className="flex flex-col gap-2">
        <Checkbox
          label={
            <>
              By tapping Submit, you agree to the following{' '}
              <Link to="/privacy" className="text-primary underline-offset-2 hover:underline">
                Privacy Policy
              </Link>{' '}
              &amp;{' '}
              <Link to="/terms" className="text-primary underline-offset-2 hover:underline">
                Terms &amp; Conditions
              </Link>
              .
            </>
          }
          required
          aria-invalid={errors.consent ? true : undefined}
          {...register('consent')}
        />
        {errors.consent?.message && (
          <p className="text-xs text-danger" role="alert">
            {errors.consent.message}
          </p>
        )}

        <Checkbox
          label="I would like to receive communication from Resolute Property Solutions via SMS, RCS SMS, Email & WhatsApp services for offers, updates & transactions."
          {...register('marketingConsent')}
        />
      </div>

    </FormShell>
  );
}




function ContactSuccess({ reduce, onReset }: { reduce: boolean; onReset: () => void }) {
  return (
    <motion.div
      initial={reduce ? false : { opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, ease: EASE_OUT_EXPO }}
      className="flex flex-col items-center gap-4 py-10 text-center"
      role="status"
      aria-live="polite"
    >
      <motion.span
        initial={reduce ? false : { scale: 0.6, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ delay: 0.1, type: 'spring', stiffness: 260, damping: 18 }}
        className="flex size-16 items-center justify-center rounded bg-success/15 text-success"
      >
        <CheckCircle2 className="size-9" aria-hidden="true" strokeWidth={2} />
      </motion.span>
      <div className="space-y-1.5">
        <h3 className="font-display text-2xl font-semibold tracking-tight">Message sent</h3>
        <p className="mx-auto max-w-md text-muted-foreground">
          Thank you for reaching out. An advisor will be in touch within one business day. A
          confirmation email is on its way to your inbox.
        </p>
      </div>
      <Button variant="outline" onClick={onReset}>
        Send another message
      </Button>
    </motion.div>
  );
}
